import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { debugLog } from '../utils/debugLog';

interface ResponsibilityCenterState {
    // Cache for responsibility centers: Map<RC Id, Ids (list of strings)>
    // Stored as array of [rcId, ids[]] tuples for serialization
    cache: Array<[string, string[]]>;
    loading: boolean;
    error: string | null;
    isLoaded: boolean;
}

const initialState: ResponsibilityCenterState = {
    cache: [],
    loading: false,
    error: null,
    isLoaded: false
};

const responsibilityCenterSlice = createSlice({
    name: 'responsibilityCenter',
    initialState,
    reducers: {
        setResponsibilityCenterCache: (state, action: PayloadAction<Array<[string, string[]]>>) => {
            if (state.isLoaded && state.cache.length > 0) {
                console.warn('⚠️ ResponsibilityCenter: Attempting to set cache when cache is already loaded. Existing cache will be replaced.');
            }
            // Payload is already an array of tuples (serializable)
            state.cache = action.payload;
            state.isLoaded = true;
            state.error = null;
        },
        addResponsibilityCenter: (state, action: PayloadAction<{ rcId: string; ids: string[] }>) => {
            const { rcId, ids } = action.payload;
            const existingIndex = state.cache.findIndex(([id]) => id === rcId);
            if (existingIndex >= 0) {
                const existingIds = state.cache[existingIndex][1];
                const idsMatch = JSON.stringify([...existingIds].sort()) === JSON.stringify([...ids].sort());

                console.warn(`⚠️ ResponsibilityCenter: Attempting to add RC "${rcId}" that already exists in cache.`, {
                    rcId,
                    existingIds: existingIds,
                    newIds: ids,
                    idsMatch,
                    cacheSize: state.cache.length,
                    isLoaded: state.isLoaded,
                    loading: state.loading,
                    // Get stack trace to see where this is being called from
                    caller: new Error().stack?.split('\n').slice(1, 7).map(line => line.trim()).join(' -> ')
                });

                // Only update if IDs are different
                if (!idsMatch) {
                    debugLog(`  ↳ Updating RC "${rcId}" with new IDs (was ${existingIds.length}, now ${ids.length})`);
                    state.cache[existingIndex] = [rcId, ids];
                } else {
                    debugLog(`  ↳ IDs match, no update needed`);
                }
            } else {
                state.cache.push([rcId, ids]);
            }
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        clearResponsibilityCenterCache: (state) => {
            state.cache = [];
            state.isLoaded = false;
            state.error = null;
        }
    }
});

export const {
    setResponsibilityCenterCache,
    addResponsibilityCenter,
    setLoading,
    setError,
    clearResponsibilityCenterCache
} = responsibilityCenterSlice.actions;

export default responsibilityCenterSlice.reducer;

